import { useEffect, useMemo, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { Approved } from './components/approved';
import { Reproved } from './components/reproved';
import { ResourceApprovalCreatedDialog } from './components/resource-approval-created-dialog';
import { catalogResource } from './utils/mount-catalog-course-payload';
import { aidResource } from './utils/mount-external-aid';
import { courseResource } from './utils/mount-external-course-payload';
import { useNotification } from '@/context/notification';
import { useWizard } from '@/context/wizard';
import { useCheckResourceEligibility } from '@/data/subsidy/policies/check-resource-eligibility';
import { EligibleResourceType } from '@/data/subsidy/policies/interfaces';
import { useRefundableAmountByResource } from '@/data/subsidy/policies/refundable-amount-by-resource';
import { useCreateResourceApproval } from '@/data/subsidy/resource-approvals';
import { ResourceTypes } from '@/models/resources';
import type { RefundRequestWizardProps } from '@/packages/subsidy/pages/refund-request/steps/resource-description/interfaces';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';
import { captureException } from '@/utils/capture-exception';

export const Summary = () => {
  const { values } = useWizard<RefundRequestWizardProps>();
  const { toast } = useNotification();
  const [approved, setApproved] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [policyRefundLimit, setPolicyRefundLimit] = useState<string | null>(
    null
  );
  const [resourceApprovalUuid, setResourceApprovalUuid] = useState<
    string | null
  >(null);

  const { mutate: checkResourceEligibility, isPending } =
    useCheckResourceEligibility();
  const { mutate: refundableAmountByResource } =
    useRefundableAmountByResource();
  const {
    mutate: createResourceApproval,
    isPending: isCreatingResourceApproval,
  } = useCreateResourceApproval();

  const formValues = values[RefundRequestWizardStepIds.ExternalItemForm];
  const resourceType = values[RefundRequestWizardStepIds.ExternalResourceType];
  const catalogForm = values[RefundRequestWizardStepIds.CatalogForm];

  const isCatalog =
    values[RefundRequestWizardStepIds.ItemSource] ===
    EligibleResourceType.Catalog;
  const subsidyPolicy = values?.selectedPolicy;

  const getAmount = () => {
    if (isCatalog) return catalogForm?.amount ?? 0;

    if (resourceType === ResourceTypes.Course) {
      return formValues?.course?.amount ?? 0;
    }

    return formValues?.learningAid?.amount ?? 0;
  };

  const totalAmount = getAmount();

  const formattedCatalogResource = useMemo(() => {
    return catalogResource({ catalogForm, resourceType });
  }, [catalogForm, resourceType]);

  const formattedExternalCourseResource = useMemo(() => {
    return courseResource({ formValues: formValues?.course, resourceType });
  }, [formValues, resourceType]);

  const formattedExternalAidResource = useMemo(() => {
    return aidResource({ formValues: formValues?.learningAid, resourceType });
  }, [formValues, resourceType]);

  const resource = isCatalog
    ? formattedCatalogResource
    : resourceType === ResourceTypes.Course
      ? formattedExternalCourseResource
      : formattedExternalAidResource;

  const handleSubmit = () => {
    createResourceApproval(
      {
        policy: { uuid: values?.selectedPolicy?.uuid || '' },
        resource,
      },
      {
        onSuccess: (data) => {
          setResourceApprovalUuid(data.resource_approval.uuid);
          setOpenDialog(true);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          toast({
            content:
              error?.response?.data?.error ||
              'Não foi possível criar a solicitação de reembolso.',
            type: 'error',
          });
          captureException({ error });
        },
      }
    );
  };

  useEffect(() => {
    checkResourceEligibility(
      {
        resource,
        refund_flow: true,
        policy: { uuid: values?.selectedPolicy?.uuid },
      },
      {
        onSuccess: () => setApproved(true),
        onError: () => setApproved(false),
      }
    );
  }, [checkResourceEligibility, values?.selectedPolicy?.uuid, resource]);

  useEffect(() => {
    refundableAmountByResource(
      {
        resourceApprovalRequest: {
          resource,
          refund_flow: true,
          policy: { uuid: values?.selectedPolicy?.uuid },
        },
        policyUuid: values?.selectedPolicy?.uuid || '',
      },
      {
        onSuccess: (data) => setPolicyRefundLimit(data.policy_refund_limit),
        onError: () => setPolicyRefundLimit(null),
      }
    );
  }, [refundableAmountByResource, values?.selectedPolicy?.uuid, resource]);

  if (isPending)
    return <Skeleton height={100} width="100%" variant="rounded" />;

  return (
    <Box>
      {approved ? (
        <Approved
          policyRefundLimit={policyRefundLimit}
          totalAmount={totalAmount}
          handleSubmit={handleSubmit}
          loading={isCreatingResourceApproval}
        />
      ) : (
        <Reproved />
      )}
      {openDialog && (
        <ResourceApprovalCreatedDialog
          resourceApprovalUuid={resourceApprovalUuid || ''}
          onClose={() => setOpenDialog(false)}
          actionsVisibility={{
            waitApproval: !subsidyPolicy?.configuration?.auto_approval_enabled,
          }}
        />
      )}
    </Box>
  );
};
